import { api } from '../../services/api';
import { requestCloseAfterTimer } from '../timer/timerFlow';
import { saveTaskProgress } from '../timer/saveTaskProgress';
import type { TimerCloseRow } from './endOfShiftModel';

/**
 * P-10e (D-85, D-63) — "הפסקה" and "החתמת יציאה" from EVERY place (the top-bar
 * control, the Home / dashboard clock card, the attendance page) open the SAME
 * window, from ONE always-mounted host (`AttendanceFlowHost`, inside
 * `GlobalTimer`), the way `timerFlow` serves the timer questions.
 *   - break: with timers running, the break window; without, the break starts;
 *   - clock-out: "סגירת יום" with the running timers on top when there are
 *     timers, suggestions or a documentation gap; otherwise the usual delayed
 *     clock-out with "ביטול".
 */

interface AttendanceFlowHandlers {
  requestBreak: () => Promise<void>;
  requestClockOut: () => Promise<void>;
}

let host: AttendanceFlowHandlers | null = null;

export function registerAttendanceFlowHost(handlers: AttendanceFlowHandlers): () => void {
  host = handlers;
  return () => { if (host === handlers) host = null; };
}

/** "הפסקה". Without a host, the break starts; the server pauses what runs (D-98, QA-4b ו'), records stay open. */
export async function requestBreak(): Promise<void> {
  if (host) return host.requestBreak();
  await api.startBreak([]);
}

/** "החתמת יציאה". Without a host, the plain clock-out (the server stops what still runs, D-66). */
export async function requestClockOut(): Promise<void> {
  if (host) return host.requestClockOut();
  await api.clockOut();
}

/**
 * After the checked timers stopped: save each task's status (not 🟢) and bar;
 * 🟢 "הושלם" runs the regular close (`requestCloseAfterTimer`). Returns the
 * titles that failed (the stop itself already happened).
 */
export async function saveRowsProgress(rows: readonly TimerCloseRow[]): Promise<string[]> {
  const failed: string[] = [];
  for (const r of rows) {
    if (!r.checked || !r.isTask) continue;
    try {
      const { complete } = await saveTaskProgress(r.ticketId, r.before, r.value);
      if (complete) requestCloseAfterTimer({ id: r.ticketId, title: r.title, task_type: 'task', parent_ticket_id: null });
    } catch {
      failed.push(r.title);
    }
  }
  return failed;
}

/**
 * The clock-out window stops the checked timers itself, first ("עוצרים את
 * המסומנים, מאשרים הצעות, ואז מחתימים יציאה"), each with its note. Returns the
 * titles that could not be stopped.
 */
export async function stopCheckedTimers(rows: readonly TimerCloseRow[]): Promise<string[]> {
  const failed: string[] = [];
  for (const r of rows) {
    if (!r.checked) continue;
    try {
      // FIX-B17 (QX-147) — a clock-out is not "the next timer stopped": no resume offer.
      await api.stopTimer(r.timerId, r.summary, { noResumeOffer: true });
    } catch (err) {
      // Stopped meanwhile (404): nothing left to stop.
      const code = (err as { error?: { code?: number } } | null)?.error?.code;
      if (code !== 404) failed.push(r.title);
    }
  }
  return failed;
}
